import React from 'react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { sanitizePinInput, ValidationResult, validateAndSanitize, strictPinSchema, lenientPinSchema, logSecurityEvent } from '@/lib/security-validation';
import { cn } from '@/lib/utils';

interface PinInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  success?: boolean;
  className?: string;
  validateStrength?: boolean; // If false, allows weak PINs for current PIN verification
}

export const PinInput = ({ value, onChange, disabled = false, error = false, success = false, className, validateStrength = true }: PinInputProps) => {
  const [validationError, setValidationError] = React.useState<string>('');

  const handleChange = (newValue: string) => {
    // Sanitize input first
    const sanitizedValue = sanitizePinInput(newValue);
    
    // Validate if we have a complete PIN (6 digits)
    if (sanitizedValue.length === 6 && validateStrength) {
      const validation: ValidationResult = validateAndSanitize(sanitizedValue, strictPinSchema, sanitizePinInput);
      
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid PIN format');
        logSecurityEvent('weak_pin_attempted', { pinLength: sanitizedValue.length });
        // Still allow the input but show warning
        onChange(sanitizedValue);
        return;
      } else {
        setValidationError('');
      }
    } else {
      setValidationError('');
    }
    
    // Always pass sanitized value
    onChange(sanitizedValue);
  };

  return (
    <div className={cn("flex flex-col items-center space-y-2", className)}>
      <InputOTP
        maxLength={6}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        pattern="[0-9]*"
        autoComplete="one-time-code"
      >
        <InputOTPGroup className="gap-4">
          {Array.from({ length: 6 }, (_, index) => (
            <InputOTPSlot
              key={index}
              index={index}
              className={cn(
                "relative w-14 h-14 text-xl font-inter font-light transition-all duration-400",
                "backdrop-blur-md bg-white/8 border border-white/25 rounded-2xl",
                "text-white/95 text-center flex items-center justify-center",
                "hover:border-white/50 hover:bg-white/12",
                "focus:border-white/70 focus:bg-white/15 focus:outline-none focus:ring-0",
                // Success state (green glow)
                success && "shadow-xl shadow-green-500/40 border-green-400/70 bg-green-500/10",
                success && "before:absolute before:-inset-1 before:rounded-2xl before:bg-gradient-to-r before:from-green-400/25 before:to-green-500/15 before:blur-sm before:opacity-70 before:-z-10",
                // Error state (red glow) - includes validation errors
                (error || validationError) && "shadow-xl shadow-red-500/40 border-red-400/70 bg-red-500/10",
                (error || validationError) && "before:absolute before:-inset-1 before:rounded-2xl before:bg-gradient-to-r before:from-red-400/25 before:to-red-500/15 before:blur-sm before:opacity-70 before:-z-10",
                // Normal filled state (darker orange glow)
                !success && !error && !validationError && value[index] && "shadow-xl shadow-orange-600/40 border-orange-500/70 bg-orange-600/10",
                !success && !error && !validationError && value[index] && "before:absolute before:-inset-1 before:rounded-2xl before:bg-gradient-to-r before:from-orange-500/25 before:to-orange-600/15 before:blur-sm before:opacity-70 before:-z-10",
                // Disabled state
                disabled && "opacity-40 cursor-not-allowed"
              )}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      
      {validationError && (
        <div className="text-sm text-red-400 bg-red-500/10 backdrop-blur-sm px-3 py-1 rounded-lg border border-red-400/30">
          ⚠️ {validationError}
        </div>
      )}
      
      {success && !validationError && (
        <div className="text-sm text-green-400">
          ✓ PIN format is secure
        </div>
      )}
    </div>
  );
};